// ─────────────────────────────────────────────────────────
// Shopify → DB catalog sync: configuration & pure parsers.
//
// Shopify is the upstream source of truth. This module holds the
// taxonomy mapping (product line, family, instrument, gauge, colour)
// and the deterministic parsers that turn a Shopify product into the
// catalog shape the site reads. No I/O here — kept pure for testing.
// ─────────────────────────────────────────────────────────

/** Product line slug → catalog family code + display family name. */
export const LINE_FAMILY: Record<string, { familyCode: string; familyName: string }> = {
  "aurora-one": { familyCode: "AUR", familyName: "AURORA ONE" },
  "ultra-sheer": { familyCode: "US", familyName: "ULTRA-SHEER" },
  "ultra-flex": { familyCode: "UF", familyName: "ULTRA-FLEX" },
  prime: { familyCode: "PRM", familyName: "PRIME" },
  "stainless-steel": { familyCode: "SS", familyName: "STAINLESS-STEEL" },
};

/**
 * Shopify line slugs that are aliases of a canonical line.
 * "aurora-premium" is the legacy handle prefix for what is now "Aurora One".
 */
export const LINE_ALIAS: Record<string, string> = {
  "aurora-premium": "aurora-one",
};

/**
 * Deprecated / non-catalog line prefixes. These are skipped silently —
 * they are expected to exist in Shopify until removed, and must never
 * surface as a validation issue (or display on the site).
 */
export const DEPRECATED_LINE_PATTERNS: RegExp[] = [
  /^arkay/,
  /^rockpack/,
  /^hybrid/,
  /^hyb-/,
];

/** Shopify product_type → instrument category. */
export const PRODUCT_TYPE_CATEGORY: Record<string, string> = {
  "Electric Guitar Strings": "electric",
  "Acoustic Guitar Strings": "acoustic",
  "Bass Strings": "bass",
  "Bass Guitar Strings": "bass",
  "Ukulele Strings": "ukulele",
  "Classical Guitar Strings": "classical",
};

/** Instrument category → short code used when generating a base SKU. */
export const CATEGORY_CODE: Record<string, string> = {
  electric: "EL",
  acoustic: "AC",
  bass: "BA",
  classical: "CL",
  ukulele: "UK",
};

/** Shopify colour option value → DB colour slug. */
export const COLOUR_SLUG: Record<string, string> = {
  "Aqua Blue": "aqua",
  Black: "black",
  Blue: "blue",
  Brown: "brown",
  Clearcoat: "clearcoat",
  "Clearcoat Silver": "clearcoat",
  "Clearcoated Phos": "phos",
  Evergreen: "evergreen",
  Gold: "gold",
  Green: "green",
  Lime: "lime",
  "Multi-Color": "multi-color",
  "Multi-Colored Pack": "multi-color",
  "Natural Steel": "natural",
  "Nitro Lime": "lime",
  "Nitro-Lime": "lime",
  "Nitro Tangerine": "orange",
  Orange: "orange",
  Pink: "pink",
  Purple: "purple",
  Red: "red",
  Silver: "silver",
  "Sky Blue": "sky-blue",
  White: "white",
  Yellow: "yellow",
};

const KNOWN_LINE_SLUGS = new Set([
  ...Object.keys(LINE_FAMILY),
  ...Object.keys(LINE_ALIAS),
]);

/** Strip Shopify's "copy-of-" duplicate prefix. */
export function normalizeHandle(handle: string): string {
  return handle.replace(/^copy-of-/, "");
}

/**
 * Resolve a Shopify product's line slug from its handle prefix.
 * Returns the canonical slug (after alias resolution) or null.
 */
export function resolveLineSlug(handle: string): string | null {
  const h = normalizeHandle(handle);
  // Longest prefix first so "aurora-premium" beats "aurora".
  const candidates = [...KNOWN_LINE_SLUGS].sort((a, b) => b.length - a.length);
  for (const slug of candidates) {
    if (h.startsWith(`${slug}-`) || h === slug) {
      return LINE_ALIAS[slug] ?? slug;
    }
  }
  return null;
}

export function isDeprecatedHandle(handle: string): boolean {
  const h = normalizeHandle(handle);
  return DEPRECATED_LINE_PATTERNS.some((re) => re.test(h));
}

/** Instrument category from Shopify product_type, else from handle keywords. */
export function detectCategory(productType: string, handle: string): string | null {
  const byType = PRODUCT_TYPE_CATEGORY[productType.trim()];
  if (byType) return byType;

  const h = normalizeHandle(handle);
  if (/ukulele/.test(h)) return "ukulele";
  if (/classical/.test(h)) return "classical";
  if (/acoustic/.test(h)) return "acoustic";
  if (/bass/.test(h)) return "bass";
  if (/electric/.test(h)) return "electric";
  if (/\d+-string/.test(h)) return "bass";
  return null;
}

/** Ukulele size from handle (soprano/concert/tenor) — used as gauge label. */
export function detectUkuleleSize(handle: string): string | null {
  const h = normalizeHandle(handle);
  if (/soprano/.test(h)) return "Soprano";
  if (/concert/.test(h)) return "Concert";
  if (/tenor/.test(h)) return "Tenor";
  return null;
}

/**
 * Gauge label from handle. Bass/guitar use the first numeric pair
 * (e.g. "45-105"), classical uses tension keywords, ukulele uses size.
 */
export function detectGaugeLabel(handle: string, category: string): string | null {
  const h = normalizeHandle(handle);

  if (category === "ukulele") return detectUkuleleSize(h);

  if (category === "classical") {
    if (/\bxht\b/.test(h) || /extra-high/.test(h)) return "Extra High Tension";
    if (/\bht\b/.test(h) || /high-tension/.test(h)) return "High Tension";
    if (/\bnt\b/.test(h) || /normal-tension/.test(h)) return "Normal Tension";
    return null;
  }

  // Label = min–max of the gauge numbers in the handle. Most handles list the
  // full individual gauge set (e.g. "09-11-16-24-32-42" → "9-42"), so min/max
  // reproduces the catalog label. The "4-string" / "5-string" tokens fall below
  // the gauge floor and are filtered out.
  const nums = [...h.matchAll(/\b0*(\d{1,3})\b/g)]
    .map((m) => parseInt(m[1], 10))
    .filter((n) => n >= 8 && n <= 150);
  if (nums.length === 0) return null;
  return `${Math.min(...nums)}-${Math.max(...nums)}`;
}
